// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, ArrowUpRight, Sparkles, AlertTriangle, Calendar } from 'lucide-react';
import { getCurrentSubscription } from '../../services/api';

interface Subscription {
  id: string;
  plan: {
    id: string;
    name: string;
    display_name?: string;
    tier: string;
    price_monthly_cents: number;
  } | null;
  status: string;
  billing_period: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  days_until_renewal: number;
}

interface BillingState {
  subscription: Subscription | null;
  usage: { messages?: { used: number; overage?: number } };
  limits: { max_messages_per_month?: number };
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const statusPillClass = (status: string) => {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'past_due':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'canceled':
    case 'unpaid':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-canvas text-ink-muted border-line';
  }
};

const SubscriptionCard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<BillingState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCurrentSubscription();
        if (!cancelled) {
          setData({
            subscription: res?.subscription || null,
            usage: res?.usage || {},
            limits: res?.limits || {},
          });
        }
      } catch (err) {
        if (!cancelled) setData({ subscription: null, usage: {}, limits: {} });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-card border border-line p-5">
        <div className="h-4 w-28 bg-canvas-inset rounded animate-pulse mb-3" />
        <div className="h-6 w-48 bg-canvas-inset rounded animate-pulse mb-4" />
        <div className="h-2 w-full bg-canvas-inset rounded animate-pulse" />
      </div>
    );
  }

  const sub = data?.subscription;

  // Empty state — no active subscription
  if (!sub || !sub.plan) {
    return (
      <div className="bg-white rounded-card border border-line p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-brand-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold text-ink">Subscription</h3>
            <p className="text-sm text-ink-dim mt-0.5">
              No active plan. Choose one to unlock chat, integrations, and analytics.
            </p>
          </div>
          <button
            onClick={() => navigate('/client/billing?tab=plans')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors flex-shrink-0"
          >
            Choose plan
            <ArrowUpRight size={12} />
          </button>
        </div>
      </div>
    );
  }

  const planName = sub.plan.display_name || sub.plan.name;
  const used = (data?.usage as any)?.messages?.used ?? 0;
  const limit = data?.limits?.max_messages_per_month || 0;
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const overLimit = used > limit && limit > 0;

  return (
    <div className="bg-white rounded-card border border-line p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
            <CreditCard size={18} className="text-brand-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-ink truncate">{planName}</h3>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border capitalize ${statusPillClass(sub.status)}`}
              >
                {sub.status}
              </span>
            </div>
            <p className="text-xs text-ink-dim mt-0.5">
              {formatCurrency(sub.plan.price_monthly_cents)}/mo · {sub.billing_period} billing
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/client/billing')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-line text-ink hover:bg-canvas transition-colors flex-shrink-0"
        >
          Manage
          <ArrowUpRight size={12} />
        </button>
      </div>

      {limit > 0 && (
        <div>
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-ink-dim">Messages this period</span>
            <span className={`font-medium ${overLimit ? 'text-red-600' : 'text-ink'}`}>
              {used.toLocaleString()} / {limit.toLocaleString()}
            </span>
          </div>
          <div className="h-1.5 bg-canvas-inset rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                overLimit ? 'bg-red-500' : pct >= 90 ? 'bg-yellow-500' : 'bg-brand-500'
              }`}
              style={{ width: `${Math.max(pct, 2)}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-xs text-ink-dim">
        <span className="inline-flex items-center gap-1.5">
          <Calendar size={12} />
          Renews {formatDate(sub.current_period_end)} ({sub.days_until_renewal}d)
        </span>
        {sub.cancel_at_period_end && (
          <span className="inline-flex items-center gap-1 text-yellow-700">
            <AlertTriangle size={12} />
            Cancels {formatDate(sub.current_period_end)}
          </span>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;
